import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateCartItemMutation } from "../api/cartApi";
import { useMutationWithFeedback } from "../../../../common/hooks/hooks";

/**
 * A custom hook to manage and debounce quantity updates for a cart item.
 * It provides feedback only on error.
 */
export const useUpdateCartItemQuantity = (
  itemKey: string,
  initialQuantity: number
) => {
  const { t } = useTranslation();
  const [updateCartItem, updateResult] = useUpdateCartItemMutation();
  const isUpdateInFlight = useRef(false); // Our manual "lock"

  const { trigger: triggerUpdate, isLoading: isUpdating } =
    useMutationWithFeedback({
      mutation: [updateCartItem, updateResult],
      errorMessages: {
        default: t(
          "feedback.cart.product.updateQuantityError",
          "Could not update item quantity."
        ),
      },
    });

  const [quantity, setQuantity] = useState(initialQuantity);

  // This effect syncs the incoming prop to our local state, BUT ONLY when
  // we are not in the middle of our own update process.
  useEffect(() => {
    // We now check our ref "lock" instead of the isUpdating flag.
    if (!isUpdateInFlight.current) {
      setQuantity(initialQuantity);
    }
  }, [initialQuantity]);

  // This effect debounces the API call and manages the "lock".
  useEffect(() => {
    const isDifferent = quantity !== initialQuantity;
    // We only want to start the debounce timer if the quantity has changed
    // and an update is not already in flight.
    if (isDifferent && !isUpdateInFlight.current) {
      const handler = setTimeout(async () => {
        if (quantity > 0) {
          // 1. Lock the UI state before starting the mutation.
          isUpdateInFlight.current = true;
          const result = await triggerUpdate({ key: itemKey, quantity });

          // If the API call failed, we need to roll back the local state.
          if (!result.success) {
            setQuantity(initialQuantity);
          }

          // 2. Unlock the UI state after the mutation is fully resolved (success or fail).
          isUpdateInFlight.current = false;
        }
      }, 500); // 500ms delay

      return () => clearTimeout(handler);
    }
  }, [quantity, initialQuantity, itemKey, triggerUpdate]);

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
  };

  return {
    quantity,
    handleQuantityChange,
    isUpdating,
  };
};
